/** Asia/Bishkek is UTC+6 all year. Wall times in the rental picker use this offset. */
export const BISHKEK_OFFSET_MS = 6 * 60 * 60 * 1000

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

export function wallParts(date) {
  const shifted = new Date(date.getTime() + BISHKEK_OFFSET_MS)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
  }
}

export function fromWall(year, month, day, hour, minute) {
  return new Date(Date.UTC(year, month - 1, day, hour, minute) - BISHKEK_OFFSET_MS)
}

export function toDatetimeLocalValue(date) {
  const parts = wallParts(date)
  const pad = (n) => String(n).padStart(2, '0')
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`
}

export function fromDatetimeLocalValue(value) {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(value))
  if (!match) return null
  return fromWall(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    Number(match[4]),
    Number(match[5])
  )
}

export function nextFullHour(now = new Date()) {
  const parts = wallParts(now)
  const hourStart = fromWall(parts.year, parts.month, parts.day, parts.hour, 0)
  return new Date(hourStart.getTime() + 60 * 60 * 1000)
}

export function rentalFactor(from, to, durationMinutes) {
  const span = to.getTime() - from.getTime()
  const minutes = Number(durationMinutes)
  if (!(span > 0) || !(minutes > 0)) return null
  return span / (minutes * 60 * 1000)
}

export function roundMoney(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100) / 100
}

export function rentalUnitPrice(base, from, to, durationMinutes) {
  const factor = rentalFactor(from, to, durationMinutes)
  if (factor == null) return null
  return roundMoney(Number(base) * factor)
}

export function rentalLineTotal(base, from, to, durationMinutes, quantity) {
  const unit = rentalUnitPrice(base, from, to, durationMinutes)
  if (unit == null) return null
  const qty = Number(quantity)
  if (!Number.isFinite(qty)) return null
  return roundMoney(unit * qty)
}

export function cartLineTotal(item) {
  const qty = Number(item?.quantity || 0)
  const price = Number(item?.price)
  if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0
  if (item?.fromAt && item?.toAt && item?.durationMinutes) {
    const total = rentalLineTotal(
      price,
      new Date(item.fromAt),
      new Date(item.toAt),
      item.durationMinutes,
      qty
    )
    return total ?? 0
  }
  return roundMoney(price * qty) ?? 0
}

function formatWall(date) {
  const parts = wallParts(date)
  const pad = (n) => String(n).padStart(2, '0')
  return `${parts.day} ${MONTHS[parts.month - 1]} ${pad(parts.hour)}:${pad(parts.minute)}`
}

function displayEnd(toExclusive) {
  const parts = wallParts(toExclusive)
  if (parts.hour === 0 && parts.minute === 0) {
    return new Date(toExclusive.getTime() - 60 * 1000)
  }
  return toExclusive
}

export function formatPeriodRange(from, toExclusive) {
  const start = from instanceof Date ? from : new Date(from)
  const end = toExclusive instanceof Date ? toExclusive : new Date(toExclusive)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || !(end > start)) return ''
  return `${formatWall(start)} — ${formatWall(displayEnd(end))}`
}

export function formatHours(from, to) {
  const hours = Math.round(((to.getTime() - from.getTime()) / 3600000) * 100) / 100
  const text = Number.isInteger(hours) ? String(hours) : String(hours)
  const abs = Math.abs(hours)
  const mod10 = abs % 10
  const mod100 = abs % 100
  let word = 'часов'
  if (mod10 === 1 && mod100 !== 11) word = 'час'
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'часа'
  return `${text} ${word}`
}

export function formatFactor(factor) {
  if (factor == null || !Number.isFinite(factor)) return ''
  const rounded = Math.round(factor * 100) / 100
  return Number.isInteger(rounded) ? String(rounded) : String(rounded)
}

export function formatDurationLabel(durationMinutes) {
  const minutes = Number(durationMinutes)
  if (!Number.isFinite(minutes) || minutes <= 0) return ''
  if (minutes % 60 === 0) return `${minutes / 60} ч`
  return `${minutes} мин`
}

function pluralRu(value, one, few, many) {
  const abs = Math.abs(Number(value))
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

function formatCount(value) {
  const rounded = Math.round(Number(value) * 100) / 100
  if (!Number.isFinite(rounded)) return ''
  const nearest = Math.round(rounded)
  const snapped = Math.abs(rounded - nearest) < 0.005 ? nearest : rounded
  return snapped.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
}

function spanPhrase(amount, one, few, many, fractionWord) {
  const rounded = Math.round(Number(amount) * 100) / 100
  const nearest = Math.round(rounded)
  const snapped = Math.abs(rounded - nearest) < 0.005 ? nearest : rounded
  const word = Number.isInteger(snapped) ? pluralRu(snapped, one, few, many) : fractionWord
  return `${formatCount(snapped)} ${word}`
}

/** How to say the rental unit in the period modal: "в сутки", "1 сутки", "сом/сутки". */
export function rentalUnitCopy(durationMinutes) {
  const minutes = Number(durationMinutes)
  if (!Number.isFinite(minutes) || minutes <= 0) return null

  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60)
    const per = days === 1 ? 'сутки' : `${formatCount(days)} ${pluralRu(days, 'сутки', 'суток', 'суток')}`
    return {
      rate: days === 1 ? 'в сутки' : `за ${per}`,
      per,
      span(factor) {
        return spanPhrase(factor * days, 'сутки', 'суток', 'суток', 'суток')
      },
    }
  }

  if (minutes % 60 === 0) {
    const hours = minutes / 60
    const per = hours === 1 ? 'час' : `${formatCount(hours)} ${pluralRu(hours, 'час', 'часа', 'часов')}`
    return {
      rate: hours === 1 ? 'в час' : `за ${per}`,
      per,
      span(factor) {
        return spanPhrase(factor * hours, 'час', 'часа', 'часов', 'часа')
      },
    }
  }

  if (minutes === 1) {
    return {
      rate: 'в минуту',
      per: 'минута',
      span(factor) {
        return spanPhrase(factor, 'минута', 'минуты', 'минут', 'минуты')
      },
    }
  }

  const per = `${formatCount(minutes)} ${pluralRu(minutes, 'минута', 'минуты', 'минут')}`
  return {
    rate: `за ${per}`,
    per,
    span(factor) {
      return spanPhrase(factor * minutes, 'минута', 'минуты', 'минут', 'минуты')
    },
  }
}

export function formatCatalogPrice(price, durationMinutes) {
  const n = Number(price)
  if (!Number.isFinite(n)) return '—'
  const money = `${n.toFixed(2)} сом`
  const duration = formatDurationLabel(durationMinutes)
  return duration ? `${money} / ${duration}` : money
}

export function durationToParts(durationMinutes) {
  const total = Math.max(0, Number(durationMinutes) || 0)
  return {
    days: Math.floor(total / (24 * 60)),
    hours: Math.floor((total % (24 * 60)) / 60),
    minutes: total % 60,
  }
}

export function partsToDuration(days, hours, minutes) {
  const total = (Number(days) || 0) * 24 * 60 + (Number(hours) || 0) * 60 + (Number(minutes) || 0)
  return total > 0 ? total : null
}

export function rentalLineKey(productId, from, to) {
  const fromIso = from instanceof Date ? from.toISOString() : new Date(from).toISOString()
  const toIso = to instanceof Date ? to.toISOString() : new Date(to).toISOString()
  return `${productId}|${fromIso}|${toIso}`
}

export function priceSentence({ base, from, to, durationMinutes, quantity = 1 }) {
  const factor = rentalFactor(from, to, durationMinutes)
  const total = rentalLineTotal(base, from, to, durationMinutes, quantity)
  if (factor == null || total == null) return ''
  const money = (n) => Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  const qtyPart = Number(quantity) > 1 ? ` × ${quantity}` : ''
  return `${formatPeriodRange(from, to)} · ${formatHours(from, to)} · ${formatFactor(factor)} × ${money(base)}${qtyPart} = ${money(total)} сом`
}

/** API steps become { from, to, available } in milliseconds, sorted and half-open. */
export function normalizeOccupancySteps(raw) {
  if (!Array.isArray(raw)) return []
  const steps = []
  for (const step of raw) {
    const from = new Date(step?.fromAt ?? step?.from).getTime()
    const to = new Date(step?.toAt ?? step?.to).getTime()
    const available = Number(step?.available)
    if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) continue
    steps.push({
      from,
      to,
      available: Number.isFinite(available) ? available : 0,
    })
  }
  steps.sort((a, b) => a.from - b.from || a.to - b.to)
  return steps
}

/**
 * free: every moment has at least `quantity` units.
 * booked: every moment has fewer.
 * partial: the stretch is split.
 * Steps are normalized milliseconds. Gaps count as booked.
 */
export function occupancyState(steps, from, to, quantity) {
  const start = from instanceof Date ? from.getTime() : Number(from)
  const end = to instanceof Date ? to.getTime() : Number(to)
  if (!(end > start)) return 'free'
  const need = Number(quantity)
  const required = Number.isFinite(need) && need > 0 ? need : 1
  let seenFree = false
  let seenBooked = false
  let cursor = start
  for (const step of steps || []) {
    if (!(step.to > step.from)) continue
    if (step.to <= cursor) continue
    if (step.from >= end) break
    if (step.from > cursor) {
      seenBooked = true
      cursor = step.from
    }
    const pieceEnd = step.to < end ? step.to : end
    if (pieceEnd > cursor) {
      if ((Number(step.available) || 0) >= required) seenFree = true
      else seenBooked = true
      cursor = pieceEnd
    }
    if (cursor >= end) break
  }
  if (cursor < end) seenBooked = true
  if (seenFree && seenBooked) return 'partial'
  if (seenBooked) return 'booked'
  return 'free'
}

export function uniquePeriodSuggestions(lines, skipProductId) {
  const seen = new Set()
  const out = []
  for (const line of lines || []) {
    if (!line?.fromAt || !line?.toAt) continue
    if (skipProductId != null && String(line.productId ?? line.id) === String(skipProductId) && lines.length === 1) {
      // still offer other products' periods; same product periods are useful too
    }
    const key = `${line.fromAt}|${line.toAt}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({
      fromAt: line.fromAt,
      toAt: line.toAt,
      productName: line.name || 'Товар',
    })
  }
  return out
}
